import { XIcon } from '@phosphor-icons/react'
import { SearchIcon } from '@primer/octicons-react'
import { useEffect, useState } from 'react'
import z from 'zod'
import * as styles from './search-input.css.ts'

export const SearchInputSizeSchema = z.enum(['default', 'small'])
export type SearchInputSize = z.infer<typeof SearchInputSizeSchema>

type SearchInputProps = {
  value?: string
  onChange?: (value: string) => unknown
  size?: SearchInputSize
  debounce?: number
  name?: string
}

export function SearchInput({ value, onChange, size = 'default', debounce = 200, name }: SearchInputProps) {
  return <DebouncedInput value={value} onChange={onChange} size={size} debounce={debounce} name={name} />
}

type DebouncedInputProps = {
  value?: string
  onChange?: (value: string) => unknown
  debounce?: number
  size?: SearchInputSize
  name?: string
}

function DebouncedInput({
  value: initialValue = '',
  onChange = () => {},
  debounce = 200,
  size,
  name,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState<string>(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      setValue('')
      e.preventDefault()
    }
  }

  return (
    <div className={styles.searchContainer({ size })}>
      <SearchIcon className={styles.icon} />
      <input
        className={styles.searchInput}
        {...props}
        value={value}
        placeholder="Search"
        aria-label={name || 'Search input'}
        onChange={e => setValue(e.target.value)}
      />
      {value.length > 0 && (
        <button onClick={() => setValue('')} onKeyDown={handleKeyDown} aria-label="Clear Search">
          <XIcon className={styles.icon} size={12} weight="light" />
        </button>
      )}
    </div>
  )
}
